import { memo } from "react";

import { UserOutline } from "@zenith/assets/icons/components";
import { getBeneficiaryInitials } from "@zenith/utils";

/**
 * Props for Initials Square Component.
 * @interface
 * @name InitialsSquareComponentProps
 * @kind interface
 */
interface InitialsSquareComponentProps {
  /**
   * Display Name of the square for Initials Square Component.
   * @property
   * @name displayName
   * @kind property
   * @memberof InitialsSquareComponentProps
   * @type {string | undefined}
   */
  displayName: string | undefined;
  /**
   * Size of the square for Initials Square Component.
   * @property
   * @name size
   * @kind property
   * @memberof InitialsSquareComponentProps
   * @type {"sm" | "md" | "lg"}
   */
  size: "sm" | "md" | "lg";
}

/**
 * Memoized Initials Square Component.
 * @constant
 * @name InitialsSquareComponent
 * @kind variable
 * @type {React.NamedExoticComponent<InitialsSquareComponentProps>}
 */
const InitialsSquareComponent: React.NamedExoticComponent<InitialsSquareComponentProps> =
  memo(
    /**
     * Initials Square Component.
     * @function
     * @name InitialsSquareComponent
     * @kind function
     * @memberof InitialsSquareComponent
     * @param {InitialsSquareComponentProps} props Props for Initials Square Component.
     * @returns {JSX.Element} Initials Square Component.
     */
    function InitialsSquareComponent(
      props: InitialsSquareComponentProps
    ): JSX.Element {
      const { displayName, size } = props;

      function renderComponent(): JSX.Element {
        switch (size) {
          case "sm":
            return (
              <div className="mr-2.5 h-8 w-8 rounded-lg bg-rose-150 p-1 text-center uppercase text-primary-300">
                {getBeneficiaryInitials(displayName as string)}
              </div>
            );

          case "md":
            return (
              <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-rose-150 p-1 text-center text-3.5xl uppercase text-primary-300">
                {displayName && getBeneficiaryInitials(displayName as string)}
              </div>
            );

          case "lg":
            return (
              <div className="inline-flex h-[80px] w-[80px] items-center justify-center rounded-lg bg-rose-300 p-4 text-center text-4xl uppercase text-primary-300">
                {displayName ? (
                  getBeneficiaryInitials(displayName as string)
                ) : (
                  <UserOutline />
                )}
              </div>
            );

          default:
            throw new Error("Size not mapped.");
        }
      }

      const component = renderComponent();

      return component;
    }
  );

export default InitialsSquareComponent;
